import React from 'react';
import { Box, Stepper, Step, StepLabel, Button, Paper, Typography, Alert } from '@mui/material';
import { ArrowBack, ArrowForward, Refresh } from '@mui/icons-material';
import { FormProvider } from 'react-hook-form';
import OfferStep from './OfferStep';
import TrafficSourceStep from './TrafficSourceStep';
import CampaignStep from './CampaignStep';
import OverviewStep from './OverviewStep';
import SuccessStep from './SuccessStep';
import { useWizardForm } from './hooks/useWizardForm';
import { useWizardData } from './hooks/useWizardData';
import { useWizardSteps } from './hooks/useWizardSteps';

function Wizard() {
  const { loading, error } = useWizardData();
  const { activeStep, steps, handleNext, handleBack, isFirstStep, reset } = useWizardSteps();
  const { form, validateStep, onSubmit, submissionResult, resetWizard } = useWizardForm(handleNext);

  const handleStepNext = async () => {
    const isValid = await validateStep(activeStep);
    if (isValid) {
      handleNext();
    }
  };

  const handleFinish = async () => {
    await onSubmit(form.getValues());
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return <OfferStep />;
      case 1:
        return <TrafficSourceStep />;
      case 2:
        return <CampaignStep />;
      case 3:
        return <OverviewStep />;
      case 4:
        return <SuccessStep result={submissionResult} />;
      default:
        return null;
    }
  };

  const handleReset = () => {
    resetWizard();
    reset();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <FormProvider {...form}>
      <Box>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Paper sx={{ p: 3, mb: 3 }}>{renderStepContent(activeStep)}</Paper>

        {activeStep !== 4 && (
          <Box display="flex" justifyContent="space-between">
            <Button disabled={isFirstStep} onClick={handleBack} startIcon={<ArrowBack />}>
              Back
            </Button>

            {activeStep === 3 ? (
              <Button variant="contained" onClick={handleFinish} endIcon={<ArrowForward />}>
                Finish
              </Button>
            ) : (
              <Button variant="contained" onClick={handleStepNext} endIcon={<ArrowForward />}>
                Next
              </Button>
            )}
          </Box>
        )}

        {activeStep === 4 && (
          <Button variant="contained" onClick={handleReset} startIcon={<Refresh />}>
            Reset Wizard
          </Button>
        )}
      </Box>
    </FormProvider>
  );
}

export default Wizard;
